import React, { useState } from "react";
import { Stack, Box, Typography, Divider, Pagination } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Skeleton from "@mui/material/Skeleton";
import CauseCard from "~/components/Cause/Card";
import CausesClient from "../api/CausesClient";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import NoItemsMessage from "~/components/NoItemsMessage";
import SearchField from '~/components/SearchField';
import { useScrollToSection } from '~/single-page/hooks/useScrollToSection';
import { useNormalizedSearch } from '~/single-page/hooks/useNormalizedSearch';

const LoadingSkeleton = () => (
  <Grid container spacing={2}>
    {Array.from(new Array(6)).map((_, index) => (
      <Grid xs={12} sm={12} md={6} key={index}>
        <Skeleton variant="rectangular" height={118} />
      </Grid>
    ))}
  </Grid>
);

export default function CausesPage() {
  const {
    data: causes = [],
    success,
    pending,
  } = useApiCall({
    apiCall: CausesClient.index,
    init: true,
  });

  useScrollToSection();

  const [searchTerm, setSearchTerm] = useState('');
  const [normalizedSearchTerm, setNormalizedSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const filteredCauses = useNormalizedSearch(causes, normalizedSearchTerm, [
    'name',
    'description',
    'ongs.name'
  ]);

  const totalPages = Math.ceil(filteredCauses.length / itemsPerPage);
  const currentCauses = filteredCauses.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (pending) {
    return <LoadingSkeleton />;
  }

  if (!success || !causes.length) {
    return (
      <Box sx={{ margin: '2rem 0' }}>
        <NoItemsMessage itemName="causa" gender="feminino" />
      </Box>
    );
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: '100%' }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, mt: 2, textAlign: 'center' }}>
          Encontre sua causa
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4, 
            textAlign: "center" 
          }}
        >
          As causas sociais são o coração da mudança que queremos ver no mundo. 
          São elas que nos movem a agir, que nos conectam com propósitos maiores e 
          nos unem em torno de objetivos comuns. Aqui no <Box component="b">Bora Impactar</Box>, 
          você encontra diversas causas apoiadas por ONGs e movimentos sociais do Recife. 
          Educação, meio ambiente, direitos humanos, cultura... Qual causa vai despertar 
          seu desejo de transformar a realidade?
        </Typography>
      </Box>

      <SearchField 
        value={searchTerm}
        onChange={(e) => { 
          setSearchTerm(e.target.value);
          setNormalizedSearchTerm(e.target.normalizedValue);
          setPage(1);
        }}
        label="Pesquisar causas..."
        placeholder="Digite o nome da causa, descrição"
      />

      <Divider sx={{ width: "100%" }} />

      <Box>
        <Grid 
          container 
          spacing={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(2, 1fr)'
            },
            gap: 2
          }}
        >
          {currentCauses.map((cause) => (
            <Grid 
              key={cause.id}
              sx={{ 
                width: '100%',
                height: '100%'
              }}
            >
              <CauseCard 
                cause={cause} 
                sx={{ height: '100%' }}
              />
            </Grid>
          ))}
        </Grid>

        {filteredCauses.length === 0 && searchTerm && (
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            py: 4 
          }}>
            <Typography color="text.secondary">
              Nenhuma causa encontrada com o termo "{searchTerm}"
            </Typography>
          </Box>
        )}

        {filteredCauses.length > itemsPerPage && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mt: 4
          }}>
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}
